import { useQuery } from "@tanstack/react-query";
import { normalizeTitle } from "../home/catalogTitleMatch.js";
import { genreBit, type PickTitle } from "./discoveryPick.js";

/**
 * The discovery files in the page, for the website (Section 26 step 4). The APK loads the same
 * two public files into discovery.db natively (DiscoveryFiles.java); the website has no native
 * side, so the page reads them itself, from our server, once and keeps them in memory.
 *
 * - `/api/discovery/meta.json`: {generatedAt, names: {id: name}, movies: [row], series: [row]};
 *   a row is [tmdbId, label, year, genres[], flags, mpa, franchise, people[], cast[], views90, studios[]].
 * - `/api/discovery/daily.json`: {trendingYear, trending: {movies, series}, popular: {movies, series}};
 *   each list is [[tmdbId, score], ...] in order.
 *
 * Nothing about the viewer or the catalog goes out. A 404 (the job has not run) or a failure is
 * "no files": Home then builds its rows from the catalog alone, as before.
 */

/** What meta.json says about one title, ready for the picking rules. */
export interface MetaTitle extends PickTitle {
  label: string;
  /** null when meta.json gives no year. */
  metaYear: number | null;
  /** home/catalogTitleMatch.ts normalizeTitle of the label, to match titles the provider gave no TMDB id. */
  titleKey: string;
}

export interface KindIndex {
  byTmdb: Map<number, MetaTitle>;
  byKey: Map<string, MetaTitle[]>;
  trending: number[];
  popular: number[];
}

export interface DiscoveryIndex {
  names: Map<number, string>;
  vod: KindIndex;
  series: KindIndex;
  trendingYear: number | null;
}

const num = (value: unknown): number => (typeof value === "number" && Number.isFinite(value) ? value : 0);

function numbers(value: unknown): number[] {
  return Array.isArray(value) ? value.filter((entry): entry is number => typeof entry === "number") : [];
}

function emptyKind(): KindIndex {
  return { byTmdb: new Map(), byKey: new Map(), trending: [], popular: [] };
}

function addRow(kind: KindIndex, row: unknown): void {
  if (!Array.isArray(row) || typeof row[0] !== "number" || typeof row[1] !== "string") return;
  const tmdb = row[0];
  if (kind.byTmdb.has(tmdb)) return;
  const label = row[1];
  let genres = 0;
  if (Array.isArray(row[3])) for (const word of row[3]) if (typeof word === "string") genres |= genreBit(word);
  const year = typeof row[2] === "number" ? row[2] : null;
  const title: MetaTitle = {
    tmdb,
    label,
    metaYear: year,
    titleKey: normalizeTitle(label),
    year: year ?? 0,
    genres,
    flags: num(row[4]),
    mpa: typeof row[5] === "string" ? row[5] : null,
    franchise: num(row[6]),
    people: numbers(row[7]),
    cast: numbers(row[8]),
    views: num(row[9]),
    studios: numbers(row[10]),
    rating: Number.NaN,
    kidsCategory: false,
    described: true,
  };
  kind.byTmdb.set(tmdb, title);
  if (title.titleKey) {
    const list = kind.byKey.get(title.titleKey) ?? [];
    list.push(title);
    kind.byKey.set(title.titleKey, list);
  }
}

function ranking(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  const out: number[] = [];
  for (const entry of value) if (Array.isArray(entry) && typeof entry[0] === "number") out.push(entry[0]);
  return out;
}

/** The two files as the page's index; either may be missing (null). */
export function parseDiscovery(meta: unknown, daily: unknown): DiscoveryIndex {
  const index: DiscoveryIndex = { names: new Map(), vod: emptyKind(), series: emptyKind(), trendingYear: null };
  const m = (meta ?? {}) as { names?: Record<string, unknown>; movies?: unknown[]; series?: unknown[] };
  if (m.names && typeof m.names === "object") {
    for (const [id, name] of Object.entries(m.names)) if (typeof name === "string") index.names.set(Number(id), name);
  }
  for (const row of m.movies ?? []) addRow(index.vod, row);
  for (const row of m.series ?? []) addRow(index.series, row);
  const d = (daily ?? {}) as {
    trendingYear?: unknown;
    trending?: { movies?: unknown; series?: unknown };
    popular?: { movies?: unknown; series?: unknown };
  };
  if (typeof d.trendingYear === "number") index.trendingYear = d.trendingYear;
  index.vod.trending = ranking(d.trending?.movies);
  index.series.trending = ranking(d.trending?.series);
  index.vod.popular = ranking(d.popular?.movies);
  index.series.popular = ranking(d.popular?.series);
  return index;
}

async function fetchJson(name: string): Promise<unknown | null> {
  const response = await fetch(`/api/discovery/${name}.json`);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`${name}.json: HTTP ${response.status}`);
  return response.json();
}

/** How often the page asks again; the server's headers keep the answer cached in between. */
const STALE_MS = 3 * 60 * 60 * 1000;

/**
 * The website's discovery index: `undefined` while loading, `null` when the job has not written
 * either file (or the server is out of reach), so the caller falls back to the catalog alone.
 */
export function useDiscoveryIndex(enabled: boolean): DiscoveryIndex | null | undefined {
  const query = useQuery({
    queryKey: ["webDiscoveryIndex"],
    queryFn: async () => {
      const [meta, daily] = await Promise.all([fetchJson("meta"), fetchJson("daily")]);
      if (meta === null && daily === null) return null;
      return parseDiscovery(meta, daily);
    },
    enabled,
    staleTime: STALE_MS,
    gcTime: STALE_MS * 2,
    retry: 1,
    // The index is large: structural sharing would walk all of it for nothing.
    structuralSharing: false,
  });
  if (!enabled) return null;
  if (query.isError) return null;
  return query.data;
}
